import { PlatformLocation } from '@angular/common';
import { HttpProgressEvent } from '@angular/common/http';
import { GraphIdentitySet } from '@fluidx/utilities/lib/graph/graph';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise, InstrumentedHttpPromiseCallback } from '../instrumentation/instrumented-http.service';
import { GraphError } from '../models/graph.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { ContextService } from '../shared-context/context.service';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { AccessTokenConsumer, AccessTokenService, CAPDialogData } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { XhrService, XhrServiceError } from './xhr.service';

class GraphResponseCache {
    private readonly cachedResponsePromises = new Map<string, Promise<InstrumentedHttpDataResponse<any>>>();

    public set(responsePromise: Promise<InstrumentedHttpDataResponse<any>>, endpoint: string, beta: boolean, strategy: FetchStrategy) {
        const key = this.key(endpoint, beta, strategy);
        this.cachedResponsePromises.set(key, responsePromise);
    }

    public has(endpoint: string, beta: boolean, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.has(key);
    }

    public get(endpoint: string, beta: boolean, strategy: FetchStrategy): Promise<InstrumentedHttpDataResponse<any>> {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.get(key);
    }

    public delete(endpoint: string, beta: boolean, strategy: FetchStrategy): boolean {
        const key = this.key(endpoint, beta, strategy);
        return this.cachedResponsePromises.delete(key);
    }

    private key(endpoint: string, beta: boolean, strategy: FetchStrategy): string {
        return `${beta ? 'beta' : 'v1.0'}/${strategy}/${endpoint}`;
    }
}

export interface ListItemVersion {
    id: string;
    lastModified: GraphIdentitySet;
    lastModifiedDateTime: string;
}

/**
 * Abstract service that hits the Graph API.
 */
export abstract class GraphService extends XhrService {
    public static readonly graphV1 = 'v1.0';
    public static readonly graphBeta = 'beta';

    private static readonly mockGraphHeader = 'ohp-mock-graph';

    private readonly graphServiceHost: string;
    protected requestConfig: HttpRequestOptions;

    private readonly cachedResponsePromises = new GraphResponseCache();

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        protected globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.Graph, contextService, officeHomeApiConfigService, platformLocation, scheduler);

        const { officeHomeApiRoot, graphApiRoot } = contextService.sharedContext;

        // If we are running in test mode then the host will be set to officeHomeApiRoot so that we handle the request in OfficeHome app.
        this.graphServiceHost = this.inMockMode ? officeHomeApiRoot : graphApiRoot;
        this.requestConfig = { headers: new Map() };
    }

    protected getGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        strategy: FetchStrategy,
        instrumentCallback?: InstrumentedHttpPromiseCallback<T | GraphError>,
        cacheResult = false,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false,
        responseType?: HttpResponseContentType,
        schedulePriority: SchedulePriority = null,
        bypassMemoryCache = false
    ): Promise<InstrumentedHttpDataResponse<T>> {
        if (!bypassMemoryCache && this.cachedResponsePromises.has(endpoint, beta, strategy)) {
            return this.cachedResponsePromises.get(endpoint, beta, strategy);
        }

        const getPromise = new Promise<InstrumentedHttpDataResponse<T>>((resolve, reject) => {
            const url = this.getGraphServiceEndpoint(endpoint, beta);
            if (!url) {
                reject(new XhrServiceError(null, 'Graph service endpoint url is not defined'));
            }

            const requestConfig = this.getRequestConfig();

            if (typeof responseType !== 'undefined') {
                requestConfig.responseType = responseType;
            } else {
                requestConfig.responseType = HttpResponseContentType.Json;
            }

            const errorCallback = (error: XhrServiceError<T | GraphError>) => {
                if (cacheResult) {
                    this.cachedResponsePromises.delete(endpoint, beta, strategy);
                }

                reject(error);
            };

            const callback = (promise: InstrumentedHttpPromise<T | GraphError>) => {
                promise
                    .instrument((result, params) => {
                        params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        if (instrumentCallback) {
                            instrumentCallback(result, params);
                        }
                    })
                    .then(response => resolve(response as InstrumentedHttpDataResponse<T>))
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.XDHsY);
                    });
            };
            super.get(
                eventName,
                url,
                callback,
                errorCallback,
                requestConfig,
                strategy,
                onDownloadProgress,
                cancellationToken,
                timeoutInMs,
                undefined,
                undefined,
                schedulePriority
            );
        });

        if (cacheResult) {
            this.cachedResponsePromises.set(getPromise, endpoint, beta, strategy);
        }

        return getPromise;
    }

    protected postGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false,
        allowInteractive: boolean = false,
        capDialogData: CAPDialogData = null,
        specialHeaders: Map<string, string> = undefined
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        const requestConfig = this.getRequestConfig();
        if (specialHeaders !== undefined) {
            specialHeaders.forEach((value: string, key: string) => {
                requestConfig.headers.set(key, value);
            });
        }

        return super.post(eventName, url, data, callback, errorCallback, requestConfig, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs, undefined, null, null, null, allowInteractive, capDialogData);
    }

    protected putGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        beta: boolean = false
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        return super.put(eventName, url, data, callback, errorCallback, this.getRequestConfig(), onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs);
    }

    protected patchGraph<T>(
        eventName: KnownInstrumentationEvents,
        endpoint: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig?: HttpRequestOptions,
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        beta: boolean = false
    ) {
        const url = this.getGraphServiceEndpoint(endpoint, beta);
        const patchRequestConfig = !!requestConfig ? requestConfig : this.getRequestConfig();
        return super.patch(eventName, url, data, callback, errorCallback, patchRequestConfig, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs, tokenConsumerOverride, resourceUrl);
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>, requestConfig: HttpRequestOptions) {
        if (paramNameInLowerCase === 'mockgraph') {
            requestConfig?.headers.set(GraphService.mockGraphHeader, value);
        }
    }

    protected getRequestConfig(overrides: HttpRequestOptions = {}): HttpRequestOptions {
        return {
            ...this.requestConfig,
            headers: new Map(this.requestConfig.headers),
            ...overrides
        };
    }

    private getGraphServiceEndpoint(endpoint: string, beta = false): string {
        return this.graphServiceHost ? `${this.graphServiceHost}/${beta ? GraphService.graphBeta : GraphService.graphV1}/${endpoint}` : '';
    }
}
